import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

interface ProfileCompletionState {
  isComplete: boolean;
  isLoading: boolean;
  missingFields: string[];
  userProfile: any;
}

export const useProfileCompletion = () => {
  const { user } = useAuth();
  const [state, setState] = useState<ProfileCompletionState>({
    isComplete: false,
    isLoading: true,
    missingFields: [],
    userProfile: null
  });

  const checkProfileCompletion = async () => {
    if (!user) {
      setState({
        isComplete: false,
        isLoading: false,
        missingFields: [],
        userProfile: null
      });
      return;
    }

    try {
      setState(prev => ({ ...prev, isLoading: true }));

      // Buscar dados do perfil do usuário
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('user_id, name, nickname, role, created_at, updated_at')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Erro ao buscar perfil:', error);
        setState({
          isComplete: false,
          isLoading: false,
          missingFields: ['name', 'nickname'],
          userProfile: null
        });
        return;
      }

      // Verificar quais campos estão faltando
      const missingFields: string[] = [];
      
      if (!profile.name || profile.name.trim() === '' || profile.name === 'Usuário') {
        missingFields.push('name');
      }
      
      if (!profile.nickname || profile.nickname.trim() === '' || profile.nickname === 'Usuário') {
        missingFields.push('nickname');
      }

      const isComplete = missingFields.length === 0;

      setState({
        isComplete,
        isLoading: false,
        missingFields,
        userProfile: profile
      });
    } catch (error) {
      console.error('Erro na verificação de perfil:', error);
      setState({
        isComplete: false,
        isLoading: false,
        missingFields: ['name', 'nickname'],
        userProfile: null
      });
    }
  };

  const updateProfile = async (name: string, nickname: string) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          name: name.trim(),
          nickname: nickname.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) {
        console.error('Erro ao atualizar perfil:', error);
        return false;
      }

      // Recarregar verificação de perfil
      await checkProfileCompletion();
      return true;

    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      return false;
    }
  };

  useEffect(() => {
    checkProfileCompletion();
  }, [user?.id]);

  return {
    ...state,
    updateProfile,
    checkProfileCompletion
  };
};

export default useProfileCompletion;
